import React, {useEffect, useState} from "react";
import {Container} from "@mui/material";

import Navigation from "../../components/Navigation";
import Header from "./Header/Header";

import {StickyContent} from "./StickyContent/StickyContent";
import {Statistics} from "./Statistics/Statistics";
import {EconomicSection} from "./Charts/EconomicSection/EconomicSection";
import {EmploymentSection} from "./Charts/EmploymentSection/EmploymentSection";
import {SummaryPage} from "./SummaryPage/SummaryPage";
import Footer from "../../components/Footer";
import {LinearGradient} from "../../components/Icons/LinearGradient";
import {fetchCountriesPerUserDataAsync} from "../../features/countries/countriesPerUserSlice";
import {useDispatch, useSelector} from "react-redux";
import {AppDispatch, RootState} from "../../store/store";
import {fetchMapDataAsync} from "../../features/map/mapApiSlice";
import {fetchStateMapAsync} from "../../features/map/stateMapSlice";
import {ArcElement, BarController, BarElement, CategoryScale, Chart, DoughnutController, Legend, LinearScale, Title, Tooltip,} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {setCaricomCountry, setCenter, setDefaultCountry, setSelectedCountry, setZoom,} from "../../features/map/mapSlice";
import {ImpactOverTimeSection} from "./Charts/ImpactOverTime/ImpactOverTimeSection";
import {CommunitySection} from "./Charts/CommunitySection/CommunitySection";
import {defaultYear, isPDFGenerationEnabled, isRegion, regionEnv} from "../../config";
import {setCurrentRegion} from "../../features/regions/regionsSlice";
import {fetchStatisticsDataAsync, setStatisticsForSelectedCountry,} from "../../features/statistics/statisticsSlice";
import {fetchStateStatisticsAsync} from "../../features/statistics/stateStatisticsSlice";
import {StatisticsState} from "../../features/statistics/types";
import {fetchPinsDataAsync} from "../../features/map/pinsSlice";
import {LATAM_COUNTRY_NAMES} from "../../features/countries/types";
import {api} from "../../api/api";
import {stateViewConfigs} from "../../data/stateViewConfigs";
import {fetchAllCountriesDataAsync} from "../../features/countries/countriesAllSlice";

Chart.register(
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Title,
    Legend,
    ChartDataLabels,
    Tooltip,
    DoughnutController,
    ArcElement
);



const MainPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const {selectedCountry, year, selectedState, selectedDistrict} = useSelector((state: RootState) => state.map);
    const countriesData = useSelector((state: RootState) => state.countriesPerUser.data);
    const regionsData = useSelector((state: RootState) => state.regions.data);
    const statisticsDataForAllCountries = useSelector((state: RootState) => state.statistics.data);
    const stateStatisticsData = useSelector((state: RootState) => state.stateStatistics.data);
    const ref = React.useRef<HTMLDivElement>(null);

    const [
        fittedStatisticsForSelectedCountry,
        setFittedStatisticsForSelectedCountry,
    ] = useState<StatisticsState["statisticsForSelectedCountry"]>(null);

    useEffect(() => {
        dispatch(fetchCountriesPerUserDataAsync(year));
        dispatch(fetchMapDataAsync(defaultYear));
    }, [dispatch, year]);

    useEffect(() => {
        if (isPDFGenerationEnabled) {
            dispatch(fetchAllCountriesDataAsync());
        }
    }, []);

    const defaultCountry = countriesData.find(
        (country) => country.country_name.toUpperCase() === regionEnv
    );
    const currentRegion = regionsData.find(
        (region) => region.name.toUpperCase() === regionEnv
    );

    useEffect(() => {
        dispatch(setDefaultCountry(defaultCountry));
        dispatch(setCurrentRegion(currentRegion));

        if (!selectedCountry) {
            dispatch(setSelectedCountry(defaultCountry));
        }
    }, [currentRegion, dispatch, defaultCountry, selectedCountry]);

    useEffect(() => {
        dispatch(fetchStatisticsDataAsync(year));
    }, [dispatch, year]);

    // Fetch state map + statistics when a state is selected
    useEffect(() => {
        if (selectedState) {
            dispatch(fetchStateMapAsync({stateId: selectedState.state_id, year}));
            dispatch(fetchStateStatisticsAsync({stateId: selectedState.state_id, year}));
        }
    }, [dispatch, selectedState, year]);

    // Center and zoom on state when county map data loads
    const stateMapData = useSelector((state: RootState) => state.stateMap.data);
    useEffect(() => {
        if (!selectedState || !stateMapData) return;

        // Custom per-state config takes priority
        const stateConfig = stateViewConfigs.get(selectedState.state_name);
        if (stateConfig) {
            dispatch(setCenter(stateConfig.center));
            dispatch(setZoom(stateConfig.zoom));
            return;
        }

        // Calculate bounding box — handle both GeoJSON FeatureCollection and TopoJSON
        let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
        const processCoord = ([lon, lat]: number[]) => {
            if (lon < minLon) minLon = lon;
            if (lon > maxLon) maxLon = lon;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
        };
        const processRing = (ring: number[][]) => ring.forEach(processCoord);

        const mapData = stateMapData as any;
        let features = mapData?.features;
        // TopoJSON: extract coordinates from arcs
        if (!features && mapData?.type === 'Topology' && mapData?.arcs) {
            const transform = mapData.transform || { scale: [1, 1], translate: [0, 0] };
            for (const arc of mapData.arcs) {
                let x = 0, y = 0;
                for (const [dx, dy] of arc) {
                    x += dx; y += dy;
                    const lon = x * transform.scale[0] + transform.translate[0];
                    const lat = y * transform.scale[1] + transform.translate[1];
                    processCoord([lon, lat]);
                }
            }
        }
        if (features) {
            features.forEach((f: any) => {
                if (!f.geometry?.coordinates) return;
                if (f.geometry.type === 'Polygon') {
                    f.geometry.coordinates.forEach(processRing);
                } else if (f.geometry.type === 'MultiPolygon') {
                    f.geometry.coordinates.forEach((poly: number[][][]) => poly.forEach(processRing));
                }
            });
        }
        if (minLon === Infinity) return;
        let spanLon = maxLon - minLon;
        let centerLon = (minLon + maxLon) / 2;
        // Handle anti-meridian crossing (e.g. Alaska: -179 to +179)
        if (spanLon > 180 && features) {
            const lons: number[] = [];
            features.forEach((f: any) => {
                const collectLon = (ring: number[][]) => ring.forEach(([lon]: number[]) => lons.push(lon));
                if (f.geometry?.type === 'Polygon') f.geometry.coordinates.forEach(collectLon);
                else if (f.geometry?.type === 'MultiPolygon') f.geometry.coordinates.forEach((p: number[][][]) => p.forEach(collectLon));
            });
            const shifted = lons.map(l => l < 0 ? l + 360 : l);
            const shiftedMin = Math.min(...shifted);
            const shiftedMax = Math.max(...shifted);
            spanLon = shiftedMax - shiftedMin;
            centerLon = (shiftedMin + shiftedMax) / 2;
            if (centerLon > 180) centerLon -= 360;
        }
        // Zoom calculation using Mercator projection math
        const PROJ_SCALE = 300;  // geoMercator scale in projectionConfig
        const DEG2RAD = Math.PI / 180;

        const mercY = (lat: number) => PROJ_SCALE * Math.log(Math.tan(Math.PI / 4 + (lat * DEG2RAD) / 2));
        const centerLat = (minLat + maxLat) / 2;
        // Offset: shift map UP (-lat) to compensate Mercator rendering bias
        const center: [number, number] = [centerLon, centerLat - 1.5];
        const SVG_W = 800;  // react-simple-maps default viewBox width
        const SVG_H = 600;  // react-simple-maps default viewBox height
        const PADDING = 0.80;  // 20% margin so state doesn't touch edges

        // Horizontal: Mercator x is linear in longitude
        const hSpan = spanLon * DEG2RAD * PROJ_SCALE;
        const vSpan = Math.abs(mercY(maxLat) - mercY(minLat));

        const zoomH = hSpan > 0 ? (SVG_W * PADDING) / hSpan : 20;
        const zoomV = vSpan > 0 ? (SVG_H * PADDING) / vSpan : 20;
        const zoom = Math.max(3, Math.min(30, Math.min(zoomH, zoomV)));

        dispatch(setCenter(center));
        dispatch(setZoom(zoom));
    }, [dispatch, selectedState, stateMapData]);

    // Wire state/district statistics to selected country statistics display
    useEffect(() => {
        if (!selectedState || !stateStatisticsData?.length) return;
        const stateStats = stateStatisticsData[0];

        if (selectedDistrict) {
            // Fetch real district data from API
            api.getDistrictStatistics(selectedState.state_id, selectedDistrict, year)
                .then(data => {
                    if (data?.length) {
                        dispatch(setStatisticsForSelectedCountry(data[0]));
                    }
                })
                .catch(e => console.error('Failed to fetch district statistics:', e));
        } else {
            dispatch(setStatisticsForSelectedCountry(stateStats));
        }
    }, [dispatch, selectedState, selectedDistrict, stateStatisticsData, year]);

    // Wire country-level statistics when no state selected
    useEffect(() => {
        if (selectedState) return;
        const fittedStatisticsForCountry = statisticsDataForAllCountries?.find(
            (statisticsForCountry) =>
                statisticsForCountry?.country_name?.toUpperCase() === selectedCountry?.country_name?.toUpperCase()
        );
        fittedStatisticsForCountry &&
        setFittedStatisticsForSelectedCountry(fittedStatisticsForCountry);
    }, [statisticsDataForAllCountries, selectedCountry, selectedState]);

    useEffect(() => {
        if (selectedState) return;
        fittedStatisticsForSelectedCountry &&
        dispatch(
            setStatisticsForSelectedCountry(fittedStatisticsForSelectedCountry)
        );
    }, [dispatch, fittedStatisticsForSelectedCountry, selectedState]);

    useEffect(() => {
        dispatch(fetchPinsDataAsync(defaultYear));
    }, [dispatch, year]);

    useEffect(() => {
        const caricomCountry = countriesData.find(
            (country) => country.country_name.toUpperCase() === LATAM_COUNTRY_NAMES.CARICOM.toUpperCase()
        );

        if (caricomCountry) {
            dispatch(setCaricomCountry(caricomCountry));
        }
    }, [countriesData, dispatch]);

    return (
        <div id="Page-id" ref={ref}
             style={{
                 display: "flex",
                 flexDirection: "column"
             }}
        >
            <LinearGradient
                width="100%"
                height="400px"
                style={{
                    position: "absolute",
                    zIndex: -1,
                    transform: "translateY(800px)",
                }}
            />
            <Navigation/>
            <Container maxWidth={"xl"}>
                <Header/>
            </Container>
            <StickyContent/>
            <Container maxWidth={"xl"}>
                <Statistics printRef={ref}/>
                <EconomicSection/>
                <EmploymentSection/>
                {isRegion.usa && !selectedDistrict && <CommunitySection/>}
                {isRegion.romania && <ImpactOverTimeSection/>}
                <SummaryPage/>
            </Container>
            <Footer/>
        </div>
    );
};
export default MainPage;
